import type { BetaAccess, BetaUsage } from './analyticsApi';
import { BETA_FEATURES, type BetaFeatureSpec } from './betaFeatures';
import { grantSubs, grantUserIds, hasAccess, metricByUser, type AccessIndex, type CountKey, type DayRange } from './betaUsageRanges';
import type { ImpersonationCandidate } from './impersonationApi';

/** Counts per table column for the range, or null when the person has no access to the feature. */
export type BetaCell = Record<string, number> | null;

export type BetaUserRow = {
  sub: string;
  listed: boolean;
  account: string;
  user: string;
  email: string;
  role: string;
  cells: Record<string, BetaCell>;
};

type RosterLookup = {
  people: Map<string, ImpersonationCandidate>;
  peopleById: Map<string, ImpersonationCandidate>;
};

function rosterLookup(roster: ImpersonationCandidate[] | null): RosterLookup {
  return {
    people: new Map((roster ?? []).map((person) => [person.sub, person])),
    peopleById: new Map((roster ?? []).flatMap((person) => (person.userId ? [[person.userId, person] as const] : []))),
  };
}

function findOnRoster(sub: string, usage: BetaUsage, index: AccessIndex, lookup: RosterLookup): ImpersonationCandidate | undefined {
  const history = usage.users.find((person) => person.sub === sub);
  return (
    [...grantSubs(index, sub), ...(history?.subs ?? [sub])].map((alias) => lookup.people.get(alias)).find((found) => found !== undefined) ??
    [...(history?.userId ? [history.userId] : []), ...grantUserIds(index, sub)].map((id) => lookup.peopleById.get(id)).find((found) => found !== undefined)
  );
}

/**
 * Every beta feature is open to every Insight user, so access is the Insight
 * user list itself: everyone on it who belongs to a district has every
 * feature. With no list to read, everyone with recorded activity counts.
 */
export function rosterAccess(roster: ImpersonationCandidate[] | null, usage: BetaUsage): BetaAccess {
  const people = roster === null ? usage.users : roster.filter((person) => person.districtId !== '');
  return {
    entries: people.flatMap((person) => BETA_FEATURES.map((feature) => ({ sub: person.sub, userId: person.userId, feature: feature.id, grantedAt: '' }))),
  };
}

const ROLE_LABELS: Record<string, string> = {
  district_admin: 'District admin',
  teacher: 'Teacher',
};

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

const byText = (a: string, b: string) => a.localeCompare(b, 'en', { sensitivity: 'base' });

/**
 * Counting all Insight users, one row per person who has ever used a beta
 * feature, since everyone else is in the "of N" alone. Counting the beta
 * list, one row per person on it, active or not.
 */
export function betaUserRows(
  usage: BetaUsage,
  index: AccessIndex,
  roster: ImpersonationCandidate[] | null,
  range: DayRange,
  features: readonly BetaFeatureSpec[] = BETA_FEATURES,
  count: CountKey = 'all',
): BetaUserRow[] {
  const subs = new Set<string>(count === 'list' ? index.grants.keys() : usage.users.map((person) => person.sub));
  const metrics = new Set(features.flatMap((feature) => feature.columns.map((column) => column.metric)));
  const sums = new Map([...metrics].map((metric) => [metric, metricByUser(usage, metric, range)]));
  const lookup = rosterLookup(roster);

  const rows = [...subs].map((sub): BetaUserRow => {
    const person = findOnRoster(sub, usage, index, lookup);
    const cells = Object.fromEntries(
      features.map((feature): [string, BetaCell] => [
        feature.id,
        hasAccess(index, sub, feature.id)
          ? Object.fromEntries(feature.columns.map((column) => [column.metric, sums.get(column.metric)?.get(sub) ?? 0]))
          : null,
      ]),
    );
    if (!person) {
      return { sub, listed: false, account: '', user: `Not on the user list (…${sub.slice(-6)})`, email: '', role: '', cells };
    }
    return {
      sub,
      listed: true,
      account: person.districtName ?? '',
      user: person.name || person.email,
      email: person.email,
      role: roleLabel(person.role),
      cells,
    };
  });

  return rows.sort((a, b) => {
    if (a.listed !== b.listed) return a.listed ? -1 : 1;
    if ((a.account === '') !== (b.account === '')) return a.account === '' ? 1 : -1;
    return byText(a.account, b.account) || byText(a.user, b.user) || byText(a.sub, b.sub);
  });
}

export function betaAccounts(rows: BetaUserRow[]): string[] {
  return [...new Set(rows.map((row) => row.account).filter((account) => account !== ''))].sort(byText);
}

export function filterBetaRows(rows: BetaUserRow[], account: string | null): BetaUserRow[] {
  return account === null ? rows : rows.filter((row) => row.account === account);
}

export function shownFeatures(features: readonly BetaFeatureSpec[], featureId: string | null): readonly BetaFeatureSpec[] {
  return featureId === null ? features : features.filter((feature) => feature.id === featureId);
}
